import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Stethoscope, Mail, Calendar, ClipboardList, LogOut, Circle, Users, CheckCircle2 } from "lucide-react";

const AVAIL_META = {
  in_clinic: { label: "In clinic", tone: "bg-emerald-100 text-[#1B5E20]" },
  on_call:   { label: "On call",   tone: "bg-amber-100 text-[#B45309]" },
  off:       { label: "Off",       tone: "bg-slate-200 text-slate-600" },
};

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DoctorProfileDialog({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/staff/doctors/${userId}`);
        if (!cancel) setData(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load profile");
        onClose();
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [userId, onClose]);

  const avail = AVAIL_META[data?.availability || "in_clinic"] || AVAIL_META.in_clinic;
  const initials = (data?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  // Build a Mon–Sun grid for the week
  const shiftGrid = (() => {
    if (!data) return null;
    const g = { Day: Array(7).fill(false), Night: Array(7).fill(false) };
    (data.week_shifts || []).forEach((s) => {
      const d = new Date(s.shift_date + "T00:00:00");
      const dow = (d.getDay() + 6) % 7; // Mon=0
      if (g[s.shift_name]) g[s.shift_name][dow] = true;
    });
    return g;
  })();

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto"
        data-testid="doctor-profile-dialog"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#1976D2]" /> Doctor profile</div>
          <button data-testid="doctor-profile-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        {loading || !data ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Header block */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[#e6f0fb] text-[#1565C0] flex items-center justify-center text-lg font-semibold flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-lg font-semibold text-slate-900 truncate" data-testid="dp-name">{data.name}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Stethoscope className="w-3.5 h-3.5" /> <span data-testid="dp-specialty">{data.specialty || "Unassigned"}</span></div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-1"><Mail className="w-3 h-3" /> <span className="tabular" data-testid="dp-email">{data.email}</span></div>
              </div>
              <span
                data-testid="dp-availability"
                className={`h-7 px-2.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 flex-shrink-0 ${avail.tone}`}
              >
                <Circle className="w-2 h-2 fill-current" /> {avail.label}
              </span>
            </div>

            {/* Workload */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard testId="dp-waiting" icon={ClipboardList} label="Waiting now" value={data.workload?.waiting ?? 0} tone="amber" />
              <StatCard testId="dp-seen-today" icon={Users} label="Seen today" value={data.workload?.seen_today ?? 0} tone="blue" />
              <StatCard testId="dp-seen-30d" icon={CheckCircle2} label="Seen (30d)" value={data.workload?.seen_30d ?? 0} tone="green" />
            </div>

            {/* Weekly roster */}
            <section>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5" /> This week's shifts
              </div>
              {!shiftGrid || (!shiftGrid.Day.some(Boolean) && !shiftGrid.Night.some(Boolean)) ? (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-4 text-center" data-testid="dp-no-shifts">
                  Not scheduled this week.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="dp-week-grid">
                  <div className="grid grid-cols-8 text-[11px] font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                    <div className="px-2 py-1.5">Shift</div>
                    {DOW.map((d) => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
                  </div>
                  {["Day", "Night"].map((s) => (
                    <div key={s} className="grid grid-cols-8 border-b border-slate-100 last:border-0">
                      <div className="px-2 py-2 text-xs font-medium text-slate-700">{s}</div>
                      {shiftGrid[s].map((on, i) => (
                        <div key={i} className="px-2 py-2 text-center">
                          {on ? (
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${s === "Day" ? "bg-[#1976D2]" : "bg-[#5E35B1]"}`} data-testid={`dp-shift-${s.toLowerCase()}-${i}`} />
                          ) : (
                            <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-100" />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Recent handovers */}
            <section>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 mb-2">
                <LogOut className="w-3.5 h-3.5" /> Recent handover notes
              </div>
              {(data.recent_handovers || []).length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-4 text-center" data-testid="dp-no-handovers">
                  No handover notes recorded yet.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="dp-handovers">
                  {data.recent_handovers.slice(0, 5).map((h) => (
                    <li key={h.handover_id} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-slate-500">
                          {h.shift_date} · <span className="font-medium">{h.shift_name}</span> · {(h.patients || []).length} patient{(h.patients || []).length === 1 ? "" : "s"}
                        </div>
                        <div className="text-[11px] text-slate-400 tabular">
                          {h.created_at ? new Date(h.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{h.text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const toneMap = {
    amber: "bg-amber-50 text-[#B45309] border-amber-100",
    blue:  "bg-blue-50 text-[#1565C0] border-blue-100",
    green: "bg-emerald-50 text-[#1B5E20] border-emerald-100",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone] || toneMap.blue}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase font-semibold tracking-wide"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-2xl font-semibold mt-1 tabular">{value}</div>
    </div>
  );
}
